import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase/config";
import { collection, onSnapshot, addDoc, updateDoc, doc, query, orderBy, deleteDoc, getDocs, where } from "firebase/firestore";

export function useNotifications(currentUser) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "notifications"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotifications(items);
      setLoading(false);
    }, (err) => {
      console.error("Error loading notifications:", err);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Filter notifications for current user's role & user name
  const getVisibleNotifications = useCallback((userRoleOrRoles, userName, includeCleared = false) => {
    // Normalize userRoles to an array of lowercase strings
    const rolesArray = Array.isArray(userRoleOrRoles) 
      ? userRoleOrRoles.map(r => r.toLowerCase()) 
      : (userRoleOrRoles ? [String(userRoleOrRoles).toLowerCase()] : []);

    return notifications.filter(n => {
      // Filter out cleared ones if includeCleared is false
      if (!includeCleared && currentUser && (n.clearedBy || []).includes(currentUser.uid)) {
        return false;
      }

      // If there is a targeted user, verify it matches current user's email or display name
      if (n.targetUser) {
        return currentUser && (
          currentUser.email === n.targetUser || 
          (currentUser.email && currentUser.email.split("@")[0].toLowerCase() === n.targetUser.toLowerCase()) ||
          (userName && userName.toLowerCase() === n.targetUser.toLowerCase())
        );
      }
      // Fallback to role filters
      if (!n.targetRoles || n.targetRoles.length === 0) return true;
      return n.targetRoles.some(r => rolesArray.some(role => role.includes(r.toLowerCase())));
    });
  }, [notifications, currentUser]);

  const getUnreadCount = useCallback((userRoleOrRoles, userName) => {
    if (!currentUser) return 0;
    const visible = getVisibleNotifications(userRoleOrRoles, userName, false);
    return visible.filter(n => !(n.readBy || []).includes(currentUser.uid)).length;
  }, [currentUser, getVisibleNotifications]);

  const markAsRead = useCallback(async (notifId) => {
    if (!currentUser) return;
    const notif = notifications.find(n => n.id === notifId);
    if (!notif) return;
    const readBy = notif.readBy || [];
    if (readBy.includes(currentUser.uid)) return;
    await updateDoc(doc(db, "notifications", notifId), { readBy: [...readBy, currentUser.uid] });
  }, [currentUser, notifications]);

  const markAllAsRead = useCallback(async (userRoleOrRoles, userName) => {
    if (!currentUser) return;
    const visible = getVisibleNotifications(userRoleOrRoles, userName, false);
    const unread = visible.filter(n => !(n.readBy || []).includes(currentUser.uid));
    const promises = unread.map(n => {
      const readBy = n.readBy || [];
      return updateDoc(doc(db, "notifications", n.id), { readBy: [...readBy, currentUser.uid] });
    });
    await Promise.all(promises);
  }, [currentUser, getVisibleNotifications]);

  const clearAll = useCallback(async (userRoleOrRoles, userName) => {
    if (!currentUser) return;
    const visible = getVisibleNotifications(userRoleOrRoles, userName, false);
    const promises = visible.map(n => {
      const clearedBy = n.clearedBy || [];
      const readBy = n.readBy || [];
      const updates = { clearedBy: [...clearedBy, currentUser.uid] };
      if (!readBy.includes(currentUser.uid)) {
        updates.readBy = [...readBy, currentUser.uid];
      }
      return updateDoc(doc(db, "notifications", n.id), updates);
    });
    await Promise.all(promises);
  }, [currentUser, getVisibleNotifications]);

  // Generate delayed shipping notifications
  const generateDelayedShippingNotifications = useCallback(async (shipments, pos) => {
    const interval = parseInt(localStorage.getItem("global_delayed_shipping_interval") || "0", 10);
    if (interval <= 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (const shipment of shipments) {
      if (!shipment.poId) continue;
      const po = pos.find(p => p.id === shipment.poId);
      if (!po || !po.items) continue;

      for (const item of po.items) {
        const itemStatus = shipment.itemStatuses?.[item.id]?.status || shipment.status || "To Order";
        if (itemStatus !== "Purchased") continue;

        // Calculate days since item moved to Purchased (orderedDate on the item or shipment)
        const itemOrderedDate = shipment.itemStatuses?.[item.id]?.orderedDate || shipment.orderedDate;
        if (!itemOrderedDate) continue;

        let purchasedDate;
        if (itemOrderedDate && typeof itemOrderedDate.toDate === 'function') {
          purchasedDate = itemOrderedDate.toDate();
        } else {
          purchasedDate = new Date(itemOrderedDate);
        }

        if (isNaN(purchasedDate.getTime())) {
          continue;
        }

        purchasedDate.setHours(0, 0, 0, 0);
        const daysDiff = Math.floor((today - purchasedDate) / (1000 * 60 * 60 * 24));

        if (isNaN(daysDiff) || daysDiff < interval) continue;

        // Check if notification already exists for this item
        const notifKey = `delayed_shipping_${shipment.id}_${item.id}`;
        const existing = notifications.find(n => n.key === notifKey);
        if (existing) continue;

        // Create notification
        await addDoc(collection(db, "notifications"), {
          key: notifKey,
          message: `${po.poNumber || "PO"} — "${item.description || "Item"}" has not shipped in ${daysDiff} days`,
          type: "delayed_shipping",
          targetRoles: ["admin", "manager", "procurement"],
          createdAt: new Date().toISOString(),
          readBy: [],
          relatedPoId: shipment.poId,
          relatedShipmentId: shipment.id
        });
      }
    }
  }, [notifications]);

  const generateOutstandingPoNotifications = useCallback(async (pos) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (const po of pos) {
      if (po.status !== "Bid Submitted") continue;

      const lastUpdatedStr = po.updatedAt || po.date || po.createdAt;
      if (!lastUpdatedStr) continue;

      let lastUpdatedDate;
      if (lastUpdatedStr && typeof lastUpdatedStr.toDate === 'function') {
        lastUpdatedDate = lastUpdatedStr.toDate();
      } else {
        lastUpdatedDate = new Date(lastUpdatedStr);
      }

      if (isNaN(lastUpdatedDate.getTime())) {
        continue;
      }

      lastUpdatedDate.setHours(0, 0, 0, 0);
      const daysDiff = Math.floor((today - lastUpdatedDate) / (1000 * 60 * 60 * 24));

      if (isNaN(daysDiff) || daysDiff < 21) continue;

      // Collect unique staff initials or usernames
      const staffList = [...new Set((po.items || []).map(item => item.staff).filter(Boolean))];

      // 1. Send notification to all Procurement users listed in line items
      for (const staff of staffList) {
        const notifKey = `outstanding_po_procurement_${po.id}_${staff}`;
        const existing = notifications.find(n => n.key === notifKey);
        if (!existing) {
          try {
            await addDoc(collection(db, "notifications"), {
              key: notifKey,
              message: `Your quote ${po.poNumber} (${po.title || "Untitled Project"}) is waiting 3 weeks, please follow up with customer.`,
              type: "po_outstanding",
              targetUser: staff,
              path: "/po",
              createdAt: new Date().toISOString(),
              readBy: []
            });
          } catch (err) {
            console.error("Error creating procurement PO outstanding notification:", err);
          }
        }
      }

      // 2. Send notification to Manager/Admin users
      const managerNotifKey = `outstanding_po_manager_${po.id}`;
      const existingManager = notifications.find(n => n.key === managerNotifKey);
      if (!existingManager) {
        try {
          await addDoc(collection(db, "notifications"), {
            key: managerNotifKey,
            message: `Your quote ${po.poNumber} (${po.title || "Untitled Project"}) is waiting 3 weeks, please follow up with customer.`,
            type: "po_outstanding",
            targetRoles: ["manager", "admin"],
            path: "/reports?tab=outstanding_pos",
            createdAt: new Date().toISOString(),
            readBy: []
          });
        } catch (err) {
          console.error("Error creating manager PO outstanding notification:", err);
        }
      }
    }
  }, [notifications]);

  return {
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    clearAll,
    generateDelayedShippingNotifications,
    generateOutstandingPoNotifications,
    getVisibleNotifications,
    getUnreadCount
  };
}
